/**
 * ComponenteDetailModal — modal grande con toda la info técnica + AAFF de un componente.
 *
 * Pensado para usarse desde Mi Producción (Elia) y desde la página de Peticiones
 * sin abandonar la pantalla.
 *
 * Props:
 *   open: bool
 *   onClose: fn
 *   componenteId: string | null
 *   onEstadoChange?: fn (cid, nuevoEstado) — opcional, lo llamamos cuando cambian estado
 */
import { componenteImgSrc } from "@/lib/cloudinary";
import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { uploadToCloudinary } from "@/lib/cloudinary";
import { descargarArchivo, nombreDescarga } from "@/lib/descargas";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Loader2, ExternalLink, Copy, Download, Upload, Trash2, Link2,
  Image as ImageIcon, FileText, FileVideo, FileArchive, Tag,
  Calendar, AlertTriangle, Building2, Package, Sparkles, Clock,
  CheckCircle2, XCircle, MinusCircle, MapPin, HardHat, Briefcase,
  Paperclip,
} from "lucide-react";

// Iter 84 (Fernando 17/jun) — Elia veía "No autenticado" al pulsar Etiqueta PDF
// desde Mis Producciones. La razón: window.open() abre la URL directamente y el
// navegador NO envía el JWT en la cabecera. Usamos fetch + blob (mismo patrón
// que MisPendientes.jsx).
async function abrirRecursoAutenticado(path) {
  try {
    const token = localStorage.getItem("dnf_access_token");
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) {
      toast.error(`No se pudo abrir el recurso (${r.status})`);
      return;
    }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    toast.error("Error abriendo el recurso");
  }
}

const MAX_UPLOAD_MB = 25;
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

const TIPO_ICON = {
  image:      ImageIcon,
  pdf:        FileText,
  video:      FileVideo,
  vector:     FileArchive,
  archivo:    FileArchive,
  url_externa: Link2,
  otro:       FileArchive,
};

const TIPO_COLOR = {
  image:       "text-sky-300 bg-sky-500/15 border-sky-500/30",
  pdf:         "text-rose-300 bg-rose-500/15 border-rose-500/30",
  video:       "text-violet-300 bg-violet-500/15 border-violet-500/30",
  vector:      "text-amber-300 bg-amber-500/15 border-amber-500/30",
  archivo:     "text-zinc-300 bg-zinc-700/30 border-zinc-600/40",
  url_externa: "text-emerald-300 bg-emerald-500/15 border-emerald-500/30",
  otro:        "text-zinc-300 bg-zinc-700/30 border-zinc-600/40",
};

const fmtBytes = (b) => {
  if (!b && b !== 0) return "—";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0; let n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n >= 100 ? 0 : 1)} ${u[i]}`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
};

/**
 * Construye URL de descarga forzando Cloudinary a respetar el nombre original.
 * Cloudinary acepta el transform `fl_attachment:<filename>` (sin extensión)
 * insertado en la URL después de `/upload/`, lo que produce un
 * `Content-Disposition: attachment; filename=<nombre>.<ext>`.
 * Para URLs externas (WeTransfer/Drive/Mega...) devolvemos la URL tal cual.
 */
const buildDownloadUrl = (a) => {
  if (!a || !a.url) return "#";
  if (a.es_url_externa) return a.url;
  try {
    const u = new URL(a.url);
    // Solo modificamos URLs de res.cloudinary.com/.../upload/...
    if (!u.hostname.includes("res.cloudinary.com")) return a.url;
    const baseName = (a.nombre || "archivo").replace(/[\\/:*?"<>|]/g, "_");
    // Quitar extensión del baseName (Cloudinary la añade desde la URL real)
    const cleanName = baseName.replace(/\.[^./\\]+$/, "");
    const slug = encodeURIComponent(cleanName).replace(/%20/g, "_").slice(0, 100);
    const flag = `fl_attachment:${slug}`;
    if (u.pathname.includes("/upload/")) {
      // Insertar el flag justo después de "/upload/"
      return a.url.replace("/upload/", `/upload/${flag}/`);
    }
    return a.url;
  } catch (_e) {
    return a.url;
  }
};

const tipoFromMime = (mime, name = "") => {
  const m = (mime || "").toLowerCase();
  const n = (name || "").toLowerCase();
  if (m.startsWith("image/")) return "image";
  if (m === "application/pdf" || n.endsWith(".pdf")) return "pdf";
  if (m.startsWith("video/")) return "video";
  if (/(\.ai|\.eps|\.svg|\.psd|\.cdr)$/.test(n)) return "vector";
  return "archivo";
};

export default function ComponenteDetailModal({
  open, onClose, componenteId, onEstadoChange,
}) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("resumen");

  // AAFF upload state
  const [uploading, setUploading] = useState(false);
  const [urlExterna, setUrlExterna] = useState("");
  const [urlNombre, setUrlNombre] = useState("");
  const [savingUrl, setSavingUrl] = useState(false);
  const dropRef = useRef(null);
  const fileInputRef = useRef(null);

  // Notas
  const [notasDraft, setNotasDraft] = useState("");
  const [savingNotas, setSavingNotas] = useState(false);

  // Iter 84 — atajo "Imprimir N etiquetas" desde el ojito.
  // Se inicializa con `bultos_recepcionados || cantidad || 1` cuando carga el componente.
  const [numEtiquetas, setNumEtiquetas] = useState(1);

  const cargar = async () => {
    if (!componenteId) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(`/componentes/${componenteId}/detalle`);
      setData(d);
      setNotasDraft(d?.componente?.notas || "");
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
      onClose?.();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && componenteId) {
      cargar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, componenteId]);

  // Reset transient state cuando se abre el modal con un componente nuevo
  // (separado del effect anterior para evitar set-state-in-effect lint)
  useEffect(() => {
    if (!open) return;
    setTab("resumen");
    setUrlExterna("");
    setUrlNombre("");
  }, [open, componenteId]);

  // Iter 84 — cuando llegan datos del componente, calculamos el default
  // sensato: bultos_recepcionados (si Sara ya recepcionó N bultos) → cantidad → 1.
  useEffect(() => {
    if (!data?.componente) return;
    const c = data.componente;
    const def = Number(c.bultos_recepcionados) || Number(c.cantidad) || 1;
    setNumEtiquetas(Math.max(1, Math.min(50, def)));
  }, [data?.componente?.id]);

  const c = data?.componente || {};
  const pet = data?.peticion || {};
  const cli = data?.cliente || {};
  const mat = data?.material || {};
  const prov = data?.proveedor || {};
  const subs = data?.subpiezas || [];
  const archivos = c?.archivos_aaff || [];
  // Total real deduplicado (propios + URL legacy + comunes) calculado en backend (utils/aaff.contar_aaff)
  const aaffTotal = data?.aaff_total ?? archivos.length;

  const m2 = useMemo(() => {
    if (c.metros_cuadrados) return Number(c.metros_cuadrados);
    if (c.ancho_cm && c.alto_cm) return (Number(c.ancho_cm) / 100) * (Number(c.alto_cm) / 100);
    return null;
  }, [c.ancho_cm, c.alto_cm, c.metros_cuadrados]);

  const dias_obj = pet?.fecha_objetivo
    ? Math.ceil((new Date(pet.fecha_objetivo) - new Date()) / 86400000)
    : null;
  const vencido = dias_obj !== null && dias_obj < 0;
  const urgente = dias_obj !== null && dias_obj >= 0 && dias_obj <= 3;

  const aprob = c?.aprobacion_aaff || null;

  const copyRef = (text) => {
    if (!text) return;
    navigator.clipboard?.writeText(text);
    toast.success("Copiado");
  };

  // ─── AAFF: subir archivo ────────────────────────────────────────────
  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let ok = 0; let fail = 0;
    for (const file of files) {
      if (file.size > MAX_UPLOAD_BYTES) {
        toast.error(`"${file.name}" excede ${MAX_UPLOAD_MB} MB — usa una URL externa.`);
        fail++;
        continue;
      }
      try {
        const tipo = tipoFromMime(file.type, file.name);
        // resource_type=auto para que Cloudinary gestione PDF/imagen/video
        const up = await uploadToCloudinary(file, "dnf/aaff", "auto");
        await api.post(`/componentes/${componenteId}/aaff`, {
          nombre: file.name,
          url: up.secure_url,
          public_id: up.public_id,
          tipo,
          mime: file.type,
          size_bytes: up.bytes ?? file.size,
          es_url_externa: false,
        });
        ok++;
      } catch (e) {
        console.error("upload error", e);
        fail++;
      }
    }
    setUploading(false);
    if (ok) toast.success(`${ok} archivo${ok > 1 ? "s" : ""} subido${ok > 1 ? "s" : ""}`);
    if (fail) toast.error(`${fail} falló al subir`);
    cargar();
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    dropRef.current?.classList.remove("ring-2", "ring-amber-400");
    handleFiles(Array.from(e.dataTransfer.files || []));
  };
  const onDragOver = (e) => {
    e.preventDefault();
    dropRef.current?.classList.add("ring-2", "ring-amber-400");
  };
  const onDragLeave = () => {
    dropRef.current?.classList.remove("ring-2", "ring-amber-400");
  };

  const guardarUrlExterna = async () => {
    const url = urlExterna.trim();
    if (!url || !urlNombre.trim()) {
      toast.error("Pon nombre y URL");
      return;
    }
    setSavingUrl(true);
    try {
      await api.post(`/componentes/${componenteId}/aaff`, {
        nombre: urlNombre.trim(),
        url,
        tipo: "url_externa",
        es_url_externa: true,
      });
      toast.success("URL externa añadida");
      setUrlExterna(""); setUrlNombre("");
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSavingUrl(false);
    }
  };

  const borrarArchivo = async (aid) => {
    if (!window.confirm("¿Borrar este archivo del componente?")) return;
    try {
      await api.delete(`/componentes/${componenteId}/aaff/${aid}`);
      toast.success("Archivo borrado");
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const guardarNotas = async () => {
    setSavingNotas(true);
    try {
      await api.patch(`/componentes/${componenteId}`, { notas: notasDraft });
      toast.success("Notas guardadas");
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando notas");
    } finally {
      setSavingNotas(false);
    }
  };

  const cambiarEstado = async (nuevo) => {
    try {
      await api.patch(`/componentes/${componenteId}`, { estado: nuevo });
      toast.success(`Estado → ${nuevo}`);
      onEstadoChange?.(componenteId, nuevo);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cambiando estado");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="bg-zinc-950 border-zinc-800 max-w-5xl max-h-[92vh] overflow-hidden flex flex-col p-0"
        data-testid="componente-detail-modal"
      >
        {loading || !data ? (
          <div className="p-12 flex items-center justify-center text-zinc-400">
            <DialogTitle className="sr-only">Cargando detalle del componente</DialogTitle>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Cargando componente…
          </div>
        ) : (
          <>
            {/* ─── CABECERA ─── */}
            <DialogHeader className="px-6 pt-5 pb-3 border-b border-zinc-900 shrink-0">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 text-[10px] uppercase tracking-wider text-zinc-500">
                    <span className="font-mono text-amber-400" data-testid="modal-peticion-codigo">{pet.codigo || "—"}</span>
                    {pet.marca && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300">
                        <Tag className="w-2.5 h-2.5" /> {pet.marca}
                      </span>
                    )}
                    {vencido && (
                      <span className="px-1.5 py-0.5 rounded bg-red-500/30 text-red-200 font-bold animate-pulse">
                        ⚠ VENCIDO
                      </span>
                    )}
                    {!vencido && urgente && (
                      <span className="px-1.5 py-0.5 rounded bg-amber-500/30 text-amber-200 font-bold">
                        ⏰ {dias_obj}d
                      </span>
                    )}
                    <span className="ml-auto font-mono text-zinc-600 hover:text-zinc-300 cursor-pointer flex items-center gap-1"
                          onClick={() => copyRef(c.id)} title="Copiar ID"
                          data-testid="modal-copy-id">
                      <Copy className="w-3 h-3" /> {String(c.id || "").slice(0, 8)}…
                    </span>
                  </div>
                  <DialogTitle className="text-2xl font-bold text-zinc-50" data-testid="modal-componente-nombre">
                    {c.nombre || "Componente"}
                  </DialogTitle>
                  <div className="mt-1 text-sm text-zinc-400 line-clamp-1">
                    {pet.nombre || ""}
                    {cli.nombre && <span className="text-zinc-600"> · </span>}
                    {cli.nombre && <span className="text-zinc-300">{cli.nombre}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span
                    className="px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-200 border border-amber-500/40"
                    data-testid="modal-estado-pill"
                  >
                    {c.estado || "—"}
                  </span>
                </div>
              </div>

              {/* Tabs */}
              <div className="mt-4 flex items-center gap-1 -mb-3 border-b border-zinc-900 -mx-6 px-6">
                {[
                  { k: "resumen", l: "Resumen" },
                  { k: "tecnica", l: "Técnica" },
                  { k: "aaff",    l: `AAFF (${aaffTotal})` },
                  { k: "notas",   l: "Notas e histórico" },
                ].map((t) => (
                  <button
                    key={t.k}
                    onClick={() => setTab(t.k)}
                    data-testid={`modal-tab-${t.k}`}
                    className={`px-3 py-2 text-xs font-semibold border-b-2 -mb-px transition-colors
                                ${tab === t.k
                                  ? "border-amber-400 text-amber-200"
                                  : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
                  >
                    {t.l}
                  </button>
                ))}
              </div>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              {/* ───────── RESUMEN ───────── */}
              {tab === "resumen" && (
                <div className="space-y-4" data-testid="modal-pane-resumen">
                  {/* KPIs rápidos */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Kpi label="Cantidad" value={c.cantidad ?? 1} icon={Package} />
                    <Kpi label="Medidas" value={c.ancho_cm && c.alto_cm ? `${c.ancho_cm}×${c.alto_cm} cm` : "—"} icon={Sparkles} />
                    <Kpi label="m²" value={m2 ? m2.toFixed(2) : "—"} icon={Sparkles} />
                    <Kpi label="F. objetivo" value={pet.fecha_objetivo || "—"} icon={Calendar} warn={vencido || urgente} />
                  </div>

                  {/* Cliente y petición */}
                  <Section title="Petición y cliente">
                    <Field label="Cliente" value={cli.nombre} />
                    <Field label="Marca" value={pet.marca} />
                    <Field label="Petición" value={pet.nombre} />
                    <Field label="Estado petición" value={pet.estado} />
                    <Field label="Contacto" value={cli.telefono || cli.email} />
                    {pet.id && (
                      <div className="md:col-span-2 mt-1">
                        <a href={`/peticiones/${pet.id}`} target="_blank" rel="noopener noreferrer"
                           className="inline-flex items-center gap-1 text-amber-300 hover:text-amber-200 text-sm"
                           data-testid="modal-link-peticion">
                          Abrir petición completa <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    )}
                  </Section>

                  {/* Aprobación AAFF */}
                  {aprob && (
                    <AprobacionBanner aprob={aprob} />
                  )}

                  {/* Línea de trabajo + espacio + montadores (Iter 70 — herencia) */}
                  <LineaTrabajoSection linea={data.linea} espacio={data.espacio} />

                  {/* Encargo (lo que escribió el comercial) */}
                  {c.cambio_grafica && (c.cambio_grafica.quitar || c.cambio_grafica.poner) && (
                    <Section title="Cambio de gráfica (permiso cliente por correo)">
                      <div className="md:col-span-2 text-sm bg-sky-500/10 border border-sky-500/40 rounded p-3 space-y-1" data-testid="componente-cambio-grafica">
                        <div className="flex flex-wrap gap-1.5">
                          {c.cambio_grafica.poner && <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-200 border border-emerald-500/40 font-semibold">PONER: {c.cambio_grafica.poner}</span>}
                          {c.cambio_grafica.quitar && <span className="px-2 py-0.5 rounded bg-red-500/20 text-red-200 border border-red-500/40 font-semibold">QUITAR: {c.cambio_grafica.quitar}</span>}
                          {c.cambio_grafica.soporte && <span className="px-2 py-0.5 rounded bg-zinc-800 text-zinc-300 border border-zinc-700">Soporte: {c.cambio_grafica.soporte}</span>}
                        </div>
                        <div className="text-[10px] text-sky-200/70">El nombre técnico «{c.nombre}» se conserva. {c.cambio_grafica.at ? `Aplicado ${c.cambio_grafica.at.slice(0, 10)}` : ""}{c.cambio_grafica.por ? ` · ${c.cambio_grafica.por}` : ""}</div>
                      </div>
                    </Section>
                  )}
                  {c.descripcion_encargo && (
                    <Section title="Encargo (briefing del comercial)">
                      <div className="md:col-span-2 whitespace-pre-wrap text-sm text-zinc-200 bg-zinc-900/60 border border-zinc-800 rounded p-3">
                        {c.descripcion_encargo}
                      </div>
                    </Section>
                  )}

                  {/* Acciones rápidas */}
                  <Section title="Acciones rápidas">
                    <div className="md:col-span-2 flex flex-wrap gap-2">
                      {["pendiente_diseno", "en_diseno", "aaff_pendiente_cliente",
                        "aaff_recibido", "en_produccion", "producido"].map((est) => (
                        <button
                          key={est}
                          onClick={() => cambiarEstado(est)}
                          disabled={c.estado === est}
                          data-testid={`modal-estado-btn-${est}`}
                          className={`text-[11px] px-2.5 py-1 rounded border transition-colors
                                     ${c.estado === est
                                       ? "border-amber-500/60 bg-amber-500/15 text-amber-200 cursor-default"
                                       : "border-zinc-800 hover:border-zinc-700 hover:bg-zinc-900 text-zinc-300"}`}
                        >
                          {est.replaceAll("_", " ")}
                        </button>
                      ))}
                    </div>
                    <div className="md:col-span-2 flex flex-wrap items-end gap-2 mt-1">
                      {/* Iter 84 — atajo "Imprimir N etiquetas" desde el ojito (Elia).
                          Default: bultos_recepcionados || cantidad || 1. Permite hasta 50. */}
                      <div className="flex items-center gap-1.5">
                        <label className="text-[10px] uppercase tracking-wider text-zinc-500" htmlFor="modal-num-etiquetas">
                          Nº
                        </label>
                        <Input
                          id="modal-num-etiquetas"
                          type="number"
                          min={1}
                          max={50}
                          value={numEtiquetas}
                          onChange={(e) => setNumEtiquetas(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
                          className="h-8 w-16 bg-zinc-900 border-zinc-800 text-center"
                          data-testid="modal-input-num-etiquetas"
                        />
                      </div>
                      <Button size="sm" variant="outline"
                        className="border-zinc-700 text-zinc-200 hover:bg-zinc-900"
                        onClick={() => {
                          const n = Math.max(1, Math.min(50, Number(numEtiquetas) || 1));
                          const qs = n > 1 ? `?n=${n}` : "";
                          abrirRecursoAutenticado(`/api/componentes/${componenteId}/etiqueta.pdf${qs}`);
                        }}
                        data-testid="modal-btn-etiqueta">
                        <Download className="w-3.5 h-3.5 mr-1.5" /> Etiqueta PDF{numEtiquetas > 1 ? ` ×${numEtiquetas}` : ""}
                      </Button>
                      <Button size="sm" variant="outline"
                        className="border-zinc-700 text-zinc-200 hover:bg-zinc-900"
                        onClick={() => abrirRecursoAutenticado(`/api/componentes/${componenteId}/qr.png`)}
                        data-testid="modal-btn-qr">
                        <ImageIcon className="w-3.5 h-3.5 mr-1.5" /> QR
                      </Button>
                    </div>
                  </Section>
                </div>
              )}

              {/* ───────── TÉCNICA ───────── */}
              {tab === "tecnica" && (
                <div className="space-y-4" data-testid="modal-pane-tecnica">
                  <Section title="Dimensiones y material">
                    <Field label="Ancho" value={c.ancho_cm ? `${c.ancho_cm} cm` : "—"} />
                    <Field label="Alto" value={c.alto_cm ? `${c.alto_cm} cm` : "—"} />
                    <Field label="m² base" value={m2 ? m2.toFixed(3) : "—"} />
                    <Field label="Merma extra" value={c.merma_pct_extra ? `${c.merma_pct_extra}%` : "—"} />
                    <Field label="Cantidad" value={c.cantidad ?? 1} />
                    <Field label="Plantilla" value={c.tipo_plantilla} />
                    <Field label="Material" value={mat.nombre || c.material_id || "—"} />
                    {mat.categoria && <Field label="Categoría material" value={mat.categoria} />}
                    {mat.ancho_imprimible_cm && <Field label="Ancho bobina" value={`${mat.ancho_imprimible_cm} cm`} />}
                    {mat.precio_venta != null && <Field label="Precio mat. m²" value={`${Number(mat.precio_venta).toFixed(2)} €`} />}
                  </Section>

                  <Section title="Producción">
                    <Field label="Fabricado por" value={c.fabricado_por} />
                    <Field label="Suministra cliente" value={c.suministrado_por_cliente ? "Sí" : "No"} />
                    <Field label="Proveedor" value={prov.nombre} />
                    <Field label="Fecha entrada" value={c.fecha_entrada} />
                    <Field label="Bulto nº" value={c.bulto_numero} />
                    <Field label="Etiqueta generada" value={c.etiqueta_generada_at ? fmtDate(c.etiqueta_generada_at) : "—"} />
                    <Field label="Coste unitario" value={c.coste_unitario != null ? `${Number(c.coste_unitario).toFixed(2)} €` : "—"} />
                    <Field label="Precio unitario" value={c.precio_unitario != null ? `${Number(c.precio_unitario).toFixed(2)} €` : "—"} />
                  </Section>

                  {c.observaciones_taller && (
                    <Section title="Observaciones taller (etiqueta)">
                      <div className="md:col-span-2 whitespace-pre-wrap text-sm text-zinc-200 bg-zinc-900/60 border border-zinc-800 rounded p-3">
                        {c.observaciones_taller}
                      </div>
                    </Section>
                  )}

                  {componenteImgSrc(c, 0) && (
                    <Section title="Imagen de referencia">
                      <div className="md:col-span-2">
                        <a href={componenteImgSrc(c, 0)} target="_blank" rel="noopener noreferrer">
                          <img src={componenteImgSrc(c, 600, 600)} alt="ref"
                            className="max-h-64 rounded border border-zinc-800 hover:border-zinc-700" />
                        </a>
                      </div>
                    </Section>
                  )}

                  {subs.length > 0 && (
                    <Section title={`Sub-piezas (${subs.length})`}>
                      <div className="md:col-span-2 space-y-1">
                        {subs.map((s) => (
                          <div key={s.id} className="flex items-center justify-between gap-2 text-xs bg-zinc-900/40 border border-zinc-800 rounded px-2 py-1.5">
                            <span className="text-zinc-200 truncate">{s.nombre}</span>
                            <span className="text-zinc-500 font-mono">
                              {s.ancho_cm && s.alto_cm ? `${s.ancho_cm}×${s.alto_cm}` : ""}
                              {s.cantidad ? ` · ${s.cantidad}u` : ""}
                            </span>
                            <span className="text-[10px] text-amber-200 bg-amber-500/15 px-1.5 py-0.5 rounded">
                              {s.estado}
                            </span>
                          </div>
                        ))}
                      </div>
                    </Section>
                  )}
                </div>
              )}

              {/* ───────── AAFF ───────── */}
              {tab === "aaff" && (
                <div className="space-y-4" data-testid="modal-pane-aaff">
                  {aprob && <AprobacionBanner aprob={aprob} />}

                  {/* AAFF global de la petición (heredado del comercial) */}
                  {(pet.aaff_url_global || pet.aaff_notas_global) && (
                    <div className="rounded border border-sky-500/30 bg-sky-500/5 p-3 text-sm">
                      <div className="flex items-center gap-2 mb-1 text-sky-200 font-semibold text-xs uppercase tracking-wider">
                        <Link2 className="w-3.5 h-3.5" /> AAFF global de la petición (comercial)
                      </div>
                      {pet.aaff_url_global && (
                        <a href={pet.aaff_url_global} target="_blank" rel="noopener noreferrer"
                          className="text-sky-300 hover:text-sky-200 underline break-all text-xs">
                          {pet.aaff_url_global}
                        </a>
                      )}
                      {pet.aaff_notas_global && (
                        <div className="mt-1 text-xs text-zinc-300 whitespace-pre-wrap">{pet.aaff_notas_global}</div>
                      )}
                    </div>
                  )}

                  {/* Sep-2026 — AAFF comunes: ficheros subidos por el cliente (portal presupuesto) y adjuntos de la petición */}
                  {(pet.aaff_comunes || []).length > 0 && (
                    <details open className="rounded border border-sky-500/30 bg-sky-500/5 p-3 text-sm" data-testid="aaff-comunes">
                      <summary className="cursor-pointer flex items-center gap-2 text-sky-200 font-semibold text-xs uppercase tracking-wider">
                        <Paperclip className="w-3.5 h-3.5" /> AAFF de la petición · archivos ({pet.aaff_comunes.length})
                      </summary>
                      <ul className="mt-2 space-y-1">
                        {pet.aaff_comunes.map((a) => {
                          const ext = (a.nombre || a.url).split("?")[0].split(".").pop().toLowerCase();
                          const visor = ["pdf", "jpg", "jpeg", "png", "webp", "gif", "svg"].includes(ext);
                          const origen = { peticion: "Petición", presupuesto_cliente: "Cliente · portal", presupuesto_dnf: "DNF · portal" }[a.origen] || a.origen;
                          return (
                            <li key={a.url} className="flex items-center gap-2 text-xs bg-zinc-950/40 rounded px-2 py-1.5" data-testid={`aaff-comun-${a.id || a.url}`}>
                              <span>📎</span>
                              <span className="font-medium text-zinc-100 truncate flex-1" title={a.nombre}>{a.nombre}</span>
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 shrink-0">{origen}</span>
                              {a.subido_at && <span className="text-[10px] text-zinc-500 shrink-0 hidden sm:inline">{a.subido_at.slice(0, 10)}</span>}
                              <a href={a.url} target="_blank" rel="noopener noreferrer" data-testid={`aaff-comun-abrir-${a.id || a.url}`}
                                className="text-[10px] uppercase px-2 py-0.5 rounded bg-sky-500/15 text-sky-200 border border-sky-500/30 hover:bg-sky-500/25 shrink-0">
                                Abrir
                              </a>
                              <button type="button" onClick={() => descargarArchivo(a)} data-testid={`aaff-comun-descargar-${a.id || a.url}`}
                                title={`Descargar como «${nombreDescarga(a)}»`}
                                className="text-[10px] uppercase px-2 py-0.5 rounded bg-amber-500/15 text-amber-200 border border-amber-500/30 hover:bg-amber-500/25 shrink-0 inline-flex items-center gap-1">
                                <Download className="w-3 h-3" /> Descargar
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </details>
                  )}

                  {/* Drop zone */}
                  <div
                    ref={dropRef}
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    className="border-2 border-dashed border-zinc-800 rounded-lg p-6 text-center bg-zinc-900/30 transition-colors"
                    data-testid="aaff-dropzone"
                  >
                    <Upload className="w-7 h-7 mx-auto mb-2 text-zinc-500" />
                    <div className="text-sm text-zinc-300">
                      Arrastra archivos aquí o
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="ml-1 text-amber-300 hover:text-amber-200 underline"
                        data-testid="aaff-browse-btn">
                        elige desde el equipo
                      </button>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-1">
                      Máx {MAX_UPLOAD_MB} MB por archivo · Imágenes / PDF / Vídeo / Vector.
                      <br />Para ficheros pesados, usa el campo URL externa de abajo (WeTransfer, Drive…).
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => handleFiles(Array.from(e.target.files || []))}
                      data-testid="aaff-file-input"
                    />
                    {uploading && (
                      <div className="mt-2 text-xs text-amber-300 flex items-center justify-center gap-1">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Subiendo…
                      </div>
                    )}
                  </div>

                  {/* URL externa */}
                  <div className="rounded border border-emerald-500/30 bg-emerald-500/5 p-3">
                    <div className="flex items-center gap-2 mb-2 text-emerald-200 font-semibold text-xs uppercase tracking-wider">
                      <Link2 className="w-3.5 h-3.5" /> Añadir URL externa (archivo pesado)
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-[1fr,2fr,auto] gap-2">
                      <Input
                        placeholder="Nombre (ej: AAFF v2)"
                        value={urlNombre}
                        onChange={(e) => setUrlNombre(e.target.value)}
                        className="bg-zinc-950 border-zinc-800 text-sm"
                        data-testid="aaff-url-nombre"
                      />
                      <Input
                        placeholder="https://we.tl/... o https://drive.google.com/..."
                        value={urlExterna}
                        onChange={(e) => setUrlExterna(e.target.value)}
                        className="bg-zinc-950 border-zinc-800 text-sm font-mono"
                        data-testid="aaff-url-input"
                      />
                      <Button
                        onClick={guardarUrlExterna}
                        disabled={savingUrl || !urlExterna.trim() || !urlNombre.trim()}
                        className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold"
                        data-testid="aaff-url-save">
                        {savingUrl ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Añadir"}
                      </Button>
                    </div>
                  </div>

                  {/* Lista de archivos */}
                  {archivos.length === 0 ? (
                    <div className="text-center text-xs text-zinc-600 italic py-6">
                      Aún no hay archivos AAFF en este componente.
                    </div>
                  ) : (
                    <div className="space-y-2" data-testid="aaff-list">
                      {archivos.map((a) => (
                        <ArchivoRow key={a.id} a={a} onDelete={borrarArchivo} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ───────── NOTAS / TIMELINE ───────── */}
              {tab === "notas" && (
                <div className="space-y-4" data-testid="modal-pane-notas">
                  <Section title="Notas internas">
                    <div className="md:col-span-2">
                      <Textarea
                        rows={5}
                        value={notasDraft}
                        onChange={(e) => setNotasDraft(e.target.value)}
                        placeholder="Notas internas del componente (visibles para Diseño/Producción)…"
                        className="bg-zinc-900 border-zinc-800 text-sm"
                        data-testid="modal-notas-textarea"
                      />
                      <div className="flex justify-end mt-2">
                        <Button
                          size="sm"
                          onClick={guardarNotas}
                          disabled={savingNotas || notasDraft === (c.notas || "")}
                          className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold"
                          data-testid="modal-notas-save">
                          {savingNotas ? "Guardando…" : "Guardar notas"}
                        </Button>
                      </div>
                    </div>
                  </Section>

                  <Section title={`Histórico (${(data.timeline || []).length})`}>
                    <div className="md:col-span-2">
                      {(data.timeline || []).length === 0 ? (
                        <div className="text-xs text-zinc-600 italic py-2">Sin eventos relacionados.</div>
                      ) : (
                        <div className="space-y-1.5">
                          {(data.timeline || []).map((ev, i) => (
                            <div key={ev.id || i}
                                 className="text-xs bg-zinc-900/40 border border-zinc-800 rounded px-2.5 py-1.5">
                              <div className="flex items-center gap-2">
                                <Clock className="w-3 h-3 text-zinc-500" />
                                <span className="text-zinc-300">{ev.descripcion}</span>
                              </div>
                              <div className="text-[10px] text-zinc-500 mt-0.5 pl-5">
                                {ev.autor_nombre || "Sistema"} · {fmtDate(ev.created_at)}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Section>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// =========== Sub-componentes ===========
const Section = ({ title, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 font-semibold">{title}</div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">{children}</div>
  </div>
);

const Field = ({ label, value }) => (
  <div className="bg-zinc-900/40 border border-zinc-800 rounded px-3 py-2">
    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
    <div className="text-sm text-zinc-100 truncate" title={String(value ?? "")}>
      {value || value === 0 ? value : <span className="text-zinc-600">—</span>}
    </div>
  </div>
);

const Kpi = ({ label, value, icon: Icon, warn }) => (
  <div className={`bg-zinc-900/50 border ${warn ? "border-amber-500/40" : "border-zinc-800"} rounded p-2.5`}>
    <div className="flex items-center justify-between text-[10px] uppercase text-zinc-500">
      {label} <Icon className={`w-3.5 h-3.5 ${warn ? "text-amber-300" : "text-zinc-500"}`} />
    </div>
    <div className={`text-lg font-bold font-mono mt-0.5 ${warn ? "text-amber-200" : "text-zinc-100"}`}>{value}</div>
  </div>
);

const AprobacionBanner = ({ aprob }) => {
  const est = (aprob?.estado || "").toLowerCase();
  const cfg = est === "aprobada"
    ? { Icon: CheckCircle2, cls: "border-emerald-500/40 bg-emerald-500/5 text-emerald-200", txt: "AAFF aprobada por el cliente" }
    : est === "rechazada"
      ? { Icon: XCircle, cls: "border-red-500/40 bg-red-500/5 text-red-200", txt: "AAFF rechazada por el cliente" }
      : { Icon: MinusCircle, cls: "border-zinc-700 bg-zinc-900/40 text-zinc-300", txt: `Estado aprobación: ${aprob?.estado || "—"}` };
  return (
    <div className={`rounded border ${cfg.cls} p-3 flex items-start gap-2 text-sm`}>
      <cfg.Icon className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-semibold">{cfg.txt}</div>
        {aprob?.por && <div className="text-[11px] opacity-80">Por: {aprob.por}</div>}
        {aprob?.fecha && <div className="text-[11px] opacity-80">{fmtDate(aprob.fecha)}</div>}
        {aprob?.comentario && (
          <div className="text-[12px] mt-1 italic opacity-90 whitespace-pre-wrap">&ldquo;{aprob.comentario}&rdquo;</div>
        )}
      </div>
    </div>
  );
};

const ArchivoRow = ({ a, onDelete }) => {
  const Icon = TIPO_ICON[a.tipo] || FileArchive;
  const colorCls = TIPO_COLOR[a.tipo] || TIPO_COLOR.archivo;
  const isImage = a.tipo === "image" && a.url;
  return (
    <div className="flex items-center gap-3 bg-zinc-900/40 border border-zinc-800 rounded p-2 hover:border-zinc-700 transition-colors"
         data-testid={`aaff-row-${a.id}`}>
      {isImage ? (
        <a href={a.url} target="_blank" rel="noopener noreferrer" className="shrink-0">
          <img src={a.url} alt="" className="w-14 h-14 object-cover rounded border border-zinc-800" />
        </a>
      ) : (
        <div className={`w-14 h-14 rounded border ${colorCls} flex items-center justify-center shrink-0`}>
          <Icon className="w-6 h-6" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm text-zinc-100 truncate" title={a.nombre}>{a.nombre}</div>
        <div className="text-[10px] text-zinc-500 flex items-center gap-2 mt-0.5">
          <span>{a.tipo}</span>
          {!a.es_url_externa && <span>· {fmtBytes(a.size_bytes)}</span>}
          {a.es_url_externa && <span className="text-emerald-400">· URL externa</span>}
          {a.subido_por_nombre && <span>· {a.subido_por_nombre}</span>}
          <span>· {fmtDate(a.created_at)}</span>
        </div>
      </div>
      {a.es_url_externa ? (
        <a href={a.url} target="_blank" rel="noopener noreferrer"
          className="text-amber-300 hover:text-amber-200 p-1.5 rounded hover:bg-amber-500/10"
          title="Abrir enlace" data-testid={`aaff-open-${a.id}`}>
          <ExternalLink className="w-4 h-4" />
        </a>
      ) : (
        <button type="button" onClick={() => descargarArchivo(a)}
          className="text-amber-300 hover:text-amber-200 p-1.5 rounded hover:bg-amber-500/10"
          title={`Descargar como «${nombreDescarga(a)}»`}
          data-testid={`aaff-open-${a.id}`}>
          <Download className="w-4 h-4" />
        </button>
      )}
      <button
        onClick={() => onDelete(a.id)}
        className="text-zinc-500 hover:text-red-300 p-1.5 rounded hover:bg-red-500/10"
        title="Borrar archivo"
        data-testid={`aaff-delete-${a.id}`}>
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};

// ─── Línea de trabajo + Espacio + Montadores (Iter 70) ──────────────
const LineaTrabajoSection = ({ linea, espacio }) => {
  const hasLinea = linea && (linea.descripcion || linea.tipo || (linea.tipos || []).length);
  const hasEspacio = espacio && (espacio.nombre || espacio.direccion);
  const hasMont = linea && (linea.montadores || []).length > 0;
  if (!hasLinea && !hasEspacio && !hasMont) return null;
  const tipos = (linea?.tipos || []).filter(Boolean);
  const tipoLabel = (t) => (t || "").replace(/_/g, " ");
  return (
    <Section title="Línea de trabajo">
      <div className="md:col-span-2 space-y-2">
        {hasLinea && (
          <div className="bg-zinc-900/40 border border-zinc-800 rounded p-3">
            <div className="flex items-center gap-2 mb-1 text-[10px] uppercase tracking-wider text-zinc-500">
              <Briefcase className="w-3 h-3" /> Trabajo
            </div>
            <div className="text-sm text-zinc-100">{linea.descripcion || <span className="text-zinc-500 italic">Sin descripción</span>}</div>
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              {tipos.length > 0
                ? tipos.map((t) => (
                    <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-500/15 text-indigo-300 border border-indigo-500/30">
                      {tipoLabel(t)}
                    </span>
                  ))
                : linea.tipo && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-500/15 text-indigo-300 border border-indigo-500/30">
                      {tipoLabel(linea.tipo)}
                    </span>
                  )}
              {linea.estado && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 uppercase tracking-wider">
                  {linea.estado}
                </span>
              )}
              {linea.fecha_programada && (
                <span className="text-[10px] text-zinc-400">📅 prog: {linea.fecha_programada.slice(0, 10)}</span>
              )}
              {linea.fecha_realizada && (
                <span className="text-[10px] text-emerald-300">✓ {linea.fecha_realizada.slice(0, 10)}</span>
              )}
              {linea.horas_estimadas != null && (
                <span className="text-[10px] text-zinc-400">⏱ {linea.horas_estimadas}h</span>
              )}
            </div>
            {linea.observaciones && (
              <div className="mt-2 pt-2 border-t border-zinc-800 text-xs text-zinc-300 whitespace-pre-wrap">
                <span className="text-zinc-500 italic">Observaciones: </span>{linea.observaciones}
              </div>
            )}
            {linea.elementos_a_montar && (
              <div className="mt-2 text-xs text-zinc-400">
                <span className="text-zinc-500 italic">Elementos a montar: </span>
                <span className="text-zinc-200">{linea.elementos_a_montar}</span>
              </div>
            )}
          </div>
        )}

        {espacio && (espacio.nombre || espacio.direccion) && (
          <div className="bg-zinc-900/40 border border-zinc-800 rounded p-3">
            <div className="flex items-center gap-2 mb-1 text-[10px] uppercase tracking-wider text-zinc-500">
              <MapPin className="w-3 h-3" /> Espacio
            </div>
            <div className="text-sm text-zinc-100">
              {espacio.nombre || "—"}
              {espacio.tipo && <span className="ml-2 text-[10px] text-zinc-400 uppercase">· {espacio.tipo}</span>}
            </div>
            {espacio.direccion && (
              <div className="text-xs text-zinc-400 mt-0.5">
                {espacio.direccion}
                {espacio.ciudad && `, ${espacio.ciudad}`}
                {espacio.provincia && ` (${espacio.provincia})`}
              </div>
            )}
            {(espacio.contacto_local || espacio.telefono_local) && (
              <div className="text-xs text-zinc-400 mt-0.5">
                {espacio.contacto_local && `👤 ${espacio.contacto_local}`}
                {espacio.telefono_local && (
                  <a href={`tel:${espacio.telefono_local}`}
                     className="ml-2 text-sky-300 hover:text-sky-200"
                     onClick={(e) => e.stopPropagation()}>
                    📞 {espacio.telefono_local}
                  </a>
                )}
              </div>
            )}
          </div>
        )}

        {linea?.montadores && linea.montadores.length > 0 && (
          <div className="bg-zinc-900/40 border border-zinc-800 rounded p-3">
            <div className="flex items-center gap-2 mb-1 text-[10px] uppercase tracking-wider text-zinc-500">
              <HardHat className="w-3 h-3" /> Montadores asignados ({linea.montadores.length})
            </div>
            <div className="flex flex-wrap gap-1.5">
              {linea.montadores.map((m) => (
                <span key={m.id}
                      className="text-[11px] px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-200 border border-orange-500/30">
                  {m.name || m.email}
                  {m.phone && (
                    <a href={`tel:${m.phone}`}
                       className="ml-1 underline text-orange-300"
                       onClick={(e) => e.stopPropagation()}>📞</a>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </Section>
  );
};

